import { defineStore } from "pinia";
import { useLocalStorage, useMouse } from "@vueuse/core";
import axios from "axios";
import APIRequest from "@/libs/cloud_auth";
import { snapdom } from '@zumer/snapdom';
import { nextTick, markRaw } from "vue";
import { useAppStore } from "../app";
import widgets from "@/assets/widgets";
import { get, set, del } from "idb-keyval";
import { normalizeWayfinderWidget } from '@/utils/presentationUtils';

/* Backup Manager */
const BACKUP_KEYS_KEY = "__backup_keys__";
const MAX_IDS = 10;
const MAX_ENTRIES = 5;

const NEW_DEFAULT = {};

const THEMES = [
  {
    type: "wayfinder",
    theme: [
      {
        primary: "#2e2e2e",
        secondary: "#5f5f5f",
        tertiary: '#5f5f5f',
        txt_primary: "#FFFFFF",
        txt_secondary: "#FFFFFF",
      },
      {
        primary: "#daeef9",
        secondary: "#0069bd",
        tertiary: "#0069bd",
        txt_primary: "#0069bd",
        txt_secondary: "#FFFFFF",
      },
      {
        primary: "#253444",
        secondary: "#00af8b",
        tertiary: "#00af8b",
        txt_primary: "#FFFFFF",
        txt_secondary: "#FFFFFF",
      },
      {
        primary: "#cfe7f2",
        secondary: "#3f3244",
        tertiary: "#3f3244",
        txt_primary: "#3f3244",
        txt_secondary: "#FFFFFF",
      },
      {
        primary: "#253444",
        secondary: "#00af8b",
        tertiary: "#FFFFFF",
        txt_primary: "#FFFFFF",
        txt_secondary: "#FFFFFF",
      },
    ],
  },
  {
    type: "flights",
    theme: [
      {
        primary: "#2e2e2e",
        secondary: "#5f5f5f",
        tertiary: "#3c3c3c",
        txt_primary: "#ffffff",
        txt_secondary: "#FFFFFF",
      },
      {
        primary: "#daeef9",
        secondary: "#0069bd",
        tertiary: "#0091db",
        txt_primary: "#0069bd",
        txt_secondary: "#FFFFFF",
      },
      {
        primary: "#253444",
        secondary: "#00af8b",
        tertiary: "#253444",
        txt_primary: "#FFFFFF",
        txt_secondary: "#FFFFFF",
      },
      {
        primary: "#cfe7f2",
        secondary: "#3f3244",
        tertiary: "#60495A",
        txt_primary: "#3f3244",
        txt_secondary: "#FFFFFF",
      },
      {
        primary: "#c7ff5c",
        secondary: "#212121",
        tertiary: "#2c364a",
        txt_primary: "#212121",
        txt_secondary: "#FFFFFF",
      },
    ],
  },
  {
    type: "islamic",
    theme: [
      {
        primary: "#212121",
        secondary: "#222732",
        txt_primary: "#EEB9B9",
        txt_secondary: "#FFFFFF",
      },
      {
        primary: "#FAEDCD",
        secondary: "#FEFAE0",
        txt_primary: "#D4A373",
        txt_secondary: "#815F0E",
      },
      {
        primary: "#344E41",
        secondary: "#3A5A40",
        txt_primary: "#A3B18A",
        txt_secondary: "#DAD7CD",
      },
      {
        primary: "#D3EDEE",
        secondary: "#F1FAEE",
        txt_primary: "#457B9D",
        txt_secondary: "#1D3557",
      },
      {
        primary: "#F27059",
        secondary: "#F25C54",
        txt_primary: "#F9C48B",
        txt_secondary: "#FEF5EC",
      },
    ],
  },
  {
    type: "rates",
    theme: [
      {
        highlight: "#C7FF5C",
        primary: "#212121",
        secondary: "#222732",
        txt_primary: "#212121",
        txt_secondary: "#FFFFFF",
      },
      {
        highlight: "#382906",
        primary: "#FAEDCD",
        secondary: "#FEFAE0",
        txt_primary: "#FEFAE0",
        txt_secondary: "#815F0E",
      },
      {
        highlight: "#A28A2A",
        primary: "#344E41",
        secondary: "#3A5A40",
        txt_primary: "#FFFFFF",
        txt_secondary: "#DAD7CD",
      },
      {
        highlight: "#275F91",
        primary: "#CEE1F2",
        secondary: "#F1FAEE",
        txt_primary: "#F1FAEE",
        txt_secondary: "#457B9D",
      },
      {
        highlight: "#5ED4C4",
        primary: "#264653",
        secondary: "#2A9D8F",
        txt_primary: "#13242A",
        txt_secondary: "#FEF5EC",
      },
    ],
  },
];

const { x: mouseX, y: mouseY } = useMouse();
const clamp = (value, min, max) => {
  return value < min ? min : value > max ? max : value;
};

export const composerStore = defineStore("composer", {
  state: () => ({
    savingBackup: false,
    savingTemplate: false,
    mounted: false,
    elementSelected: false,
    locked: false,
    interacting: false,
    galleryBucket: 0,
    galleryUpload: false,
    galleryGroup: false,
    isUploading: false,
    presentationDetails: {},
    presentation: NEW_DEFAULT,
    showOverlay: false,
    galleryType: "",
    showMenuSettings: false,
    indexElement: -1,
    indexDataArray: 0,
    configListView: false,
    temp: " ",
    settings: "default",
    preventDelete: false,
    x: 0,
    y: 0,
    color: "#fff",
    zoom: 1,
    coordinatesX: 0,
    coordinatesY: 0,
    configX: 0,
    configY: 0,
    shape: "square",
    showGrid: useLocalStorage("grid", true),
    showGallery: false,
    showTvChannels: false,
    idInputText: 0,
    videoDuration: 10,
    selectedEditor: null,
    selectedTable: null,
    onEditor: false,
    dropzone: null,
    configSlides: false,
    indexArrayTemp: -1,
    check: false,
    saveDraft: false,
    dragFromGallery: false,
    test: false,
    column_index: 0,
    row_index: 0,
    indexRow: 0,
    indexCell: 0,
    intervalIndex: [],
    intervalIndexReal: [],
    tableEditor: false,
    selectedFolder: "",
    mergeWarning: false,
    merge: false,
    history: [],
    historyIndex: -1,
    historyLimit: 10, // Specify the history limit
    customThemes: THEMES,
    elementCopy: null,
    widgetLoading: false,
    selectedItemNum: 0,
    hasVideoPlaylist: false,

    // Background Music
    hasGroupAudioPlaylist: false,
    currentAudioPlaylist: null,

    scaleFactor: 1,
  }),

  actions: {
    importPresentation(data, replace = false) {
      this.galleryType = "";
      this.showGallery = false;
      this.settings = "default";
      this.showMenuSettings = false;
      this.unSelectElements();

      setTimeout(() => {
        if (replace) {
          this.indexDataArray = 0;
          if (this.presentationDetails?.group_template) {
            // Keep group template slides, and then, add the new slides from json file to the presentation
            this.presentation.slides = this.presentation.slides
              .filter((slide) => slide?.settings?.g_lock || slide?.settings?.admin_lock || slide?.settings?.lock)
              .concat(data.slides);
          } else {
            // keep locked slides
            this.presentation.slides = this.presentation.slides.filter((slide) => slide?.settings?.admin_lock || slide?.settings?.lock).concat(data.slides);
          }
        } else {
          this.presentation.slides = this.presentation.slides.concat(
            data.slides
          );
          this.indexDataArray = this.presentation.slides.length - 1;
        }

        return this.preparePresentation();
      }, 150);
    },
    async preparePresentation() {
      if (!this.presentation?.slides) return;

      try {
        for (const [slideIndex, slide] of this.presentation.slides.entries()) {
          
          const timestamp = Date.now();
          slide.id = `${timestamp}_slide_${slideIndex}`;

          slide.elements?.forEach((element, elIndex) => {
            element.id = `${timestamp}_el_${slideIndex}_${elIndex}`;

            if (element.name === "WayFinderWidget") {
              normalizeWayfinderWidget(element);
            }
          });

          // If the thumbnail is an invalid blob URL (e.g. from an import across sessions), clear it
          if (slide.settings?.thumbnail && slide.settings.thumbnail.startsWith('blob:')) {
            slide.settings.thumbnail = '';
            slide.settings.thumbnailPending = true;
          }

          // Convert Base64 Thumbnail to Blob URL
          if (slide.settings?.thumbnail && slide.settings.thumbnail.startsWith('data:')) {
            try {
              const res = await fetch(slide.settings.thumbnail);
              const blob = await res.blob();
              const newBlobUrl = URL.createObjectURL(blob);

              if (newBlobUrl) {
                slide.settings.thumbnail = newBlobUrl;
              }
            } catch (e) {
              console.warn(`Thumbnail conversion failed for slide ${slideIndex}. Keeping original.`, e);
            }
          }
        }
        
      } catch (err) {
        console.error("Error in preparePresentation:", err);
      }
    },
    bringToFront() {
      if (this.selectedItemNum > 1) {
        const selectedItems = this.getSelectedItems;
        selectedItems.forEach((item) => {
          const index = this.slideElements.indexOf(item);
          if (index !== -1) {
            this.slideElements.splice(index, 1);
          }
          this.slideElements.push(item);
        });

        this.configListView = false;
        this.addToHistory();
        return;
      }

      if (!this.selectedWidget || this.isWidgetLocked) return;
      if (this.indexDataArray != this.slideElements.length - 1) {
        let element = this.selectedWidget;
        this.deleteElement(this.indexElement);
        this.addElement(element);
      }
      this.configListView = false;
      this.addToHistory();
    },
    sendToBack() {
      if (this.selectedItemNum > 1) {
        const selectedItems = this.getSelectedItems;
        selectedItems.forEach((item) => {
          const index = this.slideElements.indexOf(item);
          if (index !== -1) {
            this.slideElements.splice(index, 1);
          }
          this.slideElements.unshift(item);
        });

        this.indexElement = 0;
        this.showMenuSettings = true;
        this.configListView = false;
        return;
      }

      if (!this.selectedWidget || this.isWidgetLocked) return;
      let element = this.selectedWidget;
      this.deleteElement(this.indexElement);
      this.slideElements.unshift(element);
      this.indexElement = 0;
      this.showMenuSettings = true;
      this.configListView = false;
      this.addToHistory();
    },
    sendBackward() {
      if (!this.selectedWidget || this.isWidgetLocked) return;
      if (this.indexElement != 0) {
        this.presentation.slides[this.indexDataArray].elements.splice(
          this.indexElement - 1,
          0,
          this.presentation.slides[this.indexDataArray].elements.splice(
            this.indexElement,
            1
          )[0]
        );
        this.indexElement -= 1;
      }
      this.configListView = false;
      this.addToHistory();
    },
    sendForward() {
      if (!this.selectedWidget || this.isWidgetLocked) return;
      if (this.indexElement < this.slideElements.length - 1) {
        this.presentation.slides[this.indexDataArray].elements.splice(
          this.indexElement + 1,
          0,
          this.presentation.slides[this.indexDataArray].elements.splice(
            this.indexElement,
            1
          )[0]
        );
        this.indexElement += 1;
      }
      this.configListView = false;
      this.addToHistory();
    },
    copyElement() {
      if (this.selectedItemNum > 1) {
        let found = false;

        this.getSelectedItems.forEach((element) => {
          if (
            ["VideoWidget", "TVWidget", "MediaPlaylistWidget", "WayFinderWidget"].includes(
              element.name
            )
          )
            found = true;
        });
        if (found) return;

        this.elementCopy = JSON.parse(JSON.stringify(this.getSelectedItems));
        return;
      }

      if (
        !this.selectedWidget ||
        this.isWidgetLocked ||
        this.currentWidgetNBrowser ||
        (this.selectedWidget.name &&
          this.selectedWidget.name === "MediaPlaylistWidget" || this.selectedWidget.name === "WayFinderWidget")
      )
        return;

      this.elementCopy = JSON.parse(JSON.stringify(this.selectedWidget));
    },
    pasteElement() {
      if (
        !this.elementCopy ||
        this.onEditor ||
        this.selectedTable ||
        this.isSlideLocked || 
        this.elementSelected ||
        this.interacting
      )
        return;

      this.handlePaste();
      this.handleScale();
      this.addToHistory();
    },
    handlePaste(copyItems = this.elementCopy) {
      const grid = document.querySelector("#grid");
      if (!grid) return;

      const stateCopy = JSON.parse(JSON.stringify(copyItems));
      this.disableElements();
      this.unSelectElements();

      const rect = grid.getBoundingClientRect();
      const offset = 25;

      if (stateCopy.length > 1) {
        const new_group_id = Date.now();
        nextTick(() => {
          this.selectedItemNum = 0;
          stateCopy.forEach((el) => {
            el.id = Date.now();
            el.top += offset;
            el.left += offset;
            el.isSelected = true;
            if (el.group_id) {
              el.group_id = new_group_id;
            }
            this.selectedItemNum += 1;
            this.addElement(el);
          });
        });
      } else {
        if (this.settings !== stateCopy.name) {
          this.showGallery = false;
        }
        this.settings = stateCopy.name;
        // Calculate mouse position relative to the grid
        const xPos =
          (mouseX.value - rect.left) / this.zoom - stateCopy.width / 2;
        const yPos =
          (mouseY.value - rect.top) / this.zoom - stateCopy.height / 2;

        stateCopy.id = Date.now();
        stateCopy.top = clamp(yPos, 0, grid.offsetHeight - stateCopy.height);
        stateCopy.left = clamp(xPos, 0, grid.offsetWidth - stateCopy.width);
        stateCopy.active = true;
        this.addElement(stateCopy);
      }
    },
    unGroupWidgets() {
      this.getSelectedItems.forEach((el) => {
        delete el.group_id;
      });
      this.addToHistory();
    },
    groupWidgets() {
      const group_id = Date.now();
      this.getSelectedItems.forEach((el) => {
        el.group_id = group_id;
      });
      this.addToHistory();
    },
    handleScale() {
      nextTick(() => {
        let handles = document.querySelectorAll(".my-active-class .handle");
        handles.forEach((el) => {
          // If scale above 100% zoom => 1 / value < 1 ? 1 : 1 / value;
          el.style.scale = 1 / this.zoom;
        });
      });
    },
    toggleGallery(type = "image") {
      this.showGallery = !this.showGallery;
      if (!this.showGallery) this.galleryType = "";
      else this.galleryType = type;
    },
    async takeThumbnailPic(canvas, force = false) {
      const index = this.indexDataArray;
      const el = canvas;
      
      // Safety checks
      if (!el || !this.presentation?.slides?.[index]) return;
      
      const slideSettings = this.presentation.slides[index].settings;
      
      // If thumbnail is already generated and there are no pending changes, skip it!
      if (!force && slideSettings.thumbnail && !slideSettings.thumbnailPending) {
        return;
      }

      // Clone the element synchronously to capture the current state of the old slide
      // before indexDataArray is updated and Vue updates the DOM.
      const elClone = el.cloneNode(true);
      
      // Remove any unwanted interactive elements from the clone synchronously to keep it clean
      const handles = elClone.querySelectorAll('.handle, .handle-items, .container-widgetSettings, .select-box-dashed, .bounding-rectangle');
      handles.forEach(h => h.remove());

      // Strip active/selected styling (blue outline, selection border, focus styles) from the widgets
      const activeWidgets = elClone.querySelectorAll('.my-active-class, .is-selected');
      activeWidgets.forEach(widget => {
         widget.classList.remove('my-active-class', 'is-selected');
         widget.style.outline = 'none';
         widget.style.boxShadow = 'none';
      });

      const activeEditors = elClone.querySelectorAll('.text-editor');
      activeEditors.forEach(ed => ed.remove());

      const mediaWithCheckerboard = elClone.querySelectorAll('.uploaded-image, video');
      mediaWithCheckerboard.forEach(media => {
         media.style.backgroundImage = 'none';
         media.style.backgroundColor = 'transparent';
      });

      const unneeded = elClone.querySelectorAll('iframe, script');
      unneeded.forEach(u => u.remove());

      const styledElements = elClone.querySelectorAll('[style]');
      styledElements.forEach(el => {
         let style = el.getAttribute('style') || '';
         
         // Fallback for relative URL assets
         style = style.replace(/url\(['"]?(.*?)['"]?\)/g, (match, path) => {
            if (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('data:') || path.startsWith('#')) {
               return match;
            }
            let absolutePath = path;
            if (path.startsWith('/')) {
               absolutePath = window.location.origin + path;
            } else if (path.startsWith('../')) {
               absolutePath = window.location.origin + '/' + path.replace(/^\.\.\//, '');
            } else {
               absolutePath = window.location.origin + '/' + path;
            }
            return `url('${absolutePath}')`;
         });

         el.setAttribute('style', style);
      });

      const overlays = elClone.querySelectorAll('.video-overlay');
      overlays.forEach(overlay => {
         overlay.style.backgroundImage = 'none';
         overlay.style.background = 'transparent';
         overlay.style.display = 'flex';
         overlay.style.alignItems = 'center';
         overlay.style.justifyContent = 'center';
         overlay.style.width = '100%';
         overlay.style.height = '100%';
         
         const img = document.createElement('img');
         img.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNTkiIGhlaWdodD0iNDciIHZpZXdCb3g9IjAgMCA1OSA0NyIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNNTAuNzQwMSAwLjg1OTg2M0g3Ljk4MDExQzQuMDUwMTEgMC44NTk4NjMgMC44NjAxMDcgNC4wNDk4NiAwLjg2MDEwNyA3Ljk3OTg2VjM4LjczOTlDMC44NjAxMDcgNDIuNjY5OSA0LjA1MDExIDQ1Ljg1OTkgNy45ODAxMSA0NS44NTk5SDUwLjc0MDFDNTguNjcwMSA0NS44NTk5IDU3Ljg2MDEgNDIuNjY5OSA1Ny44NjAxIDM4LjczOTlWNy45Nzk4NkM1Ny44NjAxIDQuMDQ5ODYgNTQuNjcwMSAwLjg1OTg2MyA1MC43NDAxIDAuODU5ODYzWk0zOS44MjAxIDIzLjgzOTlMMjUuNTcwMSAzMi4wNjk5QzI0LjgxMDEgMzIuNTA5OSAyMy44NzAxIDMxLjk1OTkgMjMuODcwMSAzMS4wODk5VjE0LjYyOTlDMjMuODcwMSAxMy43NTk5IDI0LjgyMDEgMTMuMjA5OSAyNS41NzAxIDEzLjY0OTlMMzkuODIwMSAyMS44Nzk5QzQwLjU4MDEgMjIuMzE5OSA0MC41ODAxIDIzLjQwOTkgMzkuODIwMSAyMy44NDk5VjIzLjgzOTlaIiBmaWxsPSJ3aGl0ZSIgc3Ryb2tlPSIjNDg0ODQ4IiBzdHJva2Utd2lkdGg9IjEuNzMiLz48L3N2Zz4=';
         img.style.width = '30%';
         img.style.height = '30%';
         img.style.objectFit = 'contain';
         img.style.margin = 'auto';
         
         overlay.innerHTML = '';
         overlay.appendChild(img);
      });

      const images = elClone.querySelectorAll('img');
      images.forEach(img => {
         const src = img.getAttribute('src');
         if (src && !src.startsWith('http://') && !src.startsWith('https://') && !src.startsWith('data:')) {
            if (src.startsWith('/')) {
               img.src = window.location.origin + src;
            } else if (src.startsWith('../')) {
               img.src = window.location.origin + '/' + src.replace(/^\.\.\//, '');
            } else {
               img.src = window.location.origin + '/' + src;
            }
         }
      });

      // Create a temporary off-screen container that hides the clone from the user's view,
      // but keeps the clone itself at 100% opacity and full size so snapdom captures it perfectly.
      const container = document.createElement('div');
      container.style.position = 'fixed';
      container.style.top = '0px';
      container.style.left = '0px';
      container.style.width = '0px';
      container.style.height = '0px';
      container.style.overflow = 'hidden';
      container.style.zIndex = '-9999';
      container.style.pointerEvents = 'none';

      // Set elClone to full size and opacity
      elClone.style.position = 'absolute';
      elClone.style.top = '0px';
      elClone.style.left = '0px';
      elClone.style.width = el.style.width || '1920px';
      elClone.style.height = el.style.height || '1080px';
      elClone.style.opacity = '1';
      elClone.style.pointerEvents = 'none';

      container.appendChild(elClone);
      document.body.appendChild(container);

      await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      
      // Defer heavy snapdom serialization until browser is completely idle to prevent UI freezes
      if (window.requestIdleCallback) {
        await new Promise(resolve => window.requestIdleCallback(resolve, { timeout: 2000 }));
      } else {
        await new Promise(resolve => setTimeout(resolve, 300));
      }
      
      try {
        // Clean up previous blob to avoid memory leaks
        if (slideSettings.thumbnail && slideSettings.thumbnail.startsWith('blob:')) {
          URL.revokeObjectURL(slideSettings.thumbnail);
        }
        
        const blob = await snapdom.toBlob(elClone, {
          type: 'jpg',   // JPG encodes faster than PNG
          quality: 0.3,         
          scale: 0.25,   
          cache: 'soft',
          compress: true, // downsample images for faster rendering
          
          exclude: [
            '.handle', 
            '.container-widgetSettings', 
            '.video-tag', 
            'video', 
            'iframe', 
            'script'
          ],
          excludeMode: 'remove',
          
          outerShadows: false,
          outerTransforms: false,
          
          fast: true,
          style: {
            animation: 'none',      
            backdropFilter: 'none'
          }
        });
        
        if (blob) {
          slideSettings.thumbnail = URL.createObjectURL(blob);
          slideSettings.thumbnailPending = false;
        }
        
      } catch (error) {
        console.error("Snapdom capture error:", error);
      } finally {
        // Always clean up the clone and its parent container from the body
        if (elClone && elClone.parentNode) {
          elClone.parentNode.removeChild(elClone);
        }
        if (container && container.parentNode) {
          container.parentNode.removeChild(container);
        }
      }
    },
    getTheme(name) {
      return this.customThemes.find((theme) => theme.type === name).theme;
    },
    resetHistory() {
      this.history = [];
      this.historyIndex = -1;
    },
    addToHistory() {
      if (this.currentSlide?.settings && this.historyIndex >= 0) {
        this.currentSlide.settings.thumbnailPending = true;
      }
      nextTick().then(() => {
        // Serialize to strip Proxy reactivity overhead quickly
        const stateSnapshot = JSON.parse(JSON.stringify(
          this.presentation?.slides?.map((slide) => ({
            ...slide,
            elements: slide.elements.map((element) => ({
              ...element,
              isSelected: false,
              active: false,
            })),
          })) || []
        ));

        this.history = this.history.slice(0, this.historyIndex + 1);
        this.history.push(markRaw(stateSnapshot));
        this.historyIndex++;

        if (this.history.length > this.historyLimit) {
          this.history.shift();
          this.historyIndex--;
        }
      });
    },
    undo() { this.shiftHistory(-1); },
    redo() { this.shiftHistory(1); },
    shiftHistory(step) {
      if (document.querySelector(".vue-drag-resize-rotate.dragging")) return;
      const nextIndex = this.historyIndex + step;
      if (nextIndex < 0 || nextIndex >= this.history.length) return;

      this.undoRedoCommonTasks();

      const currentThumbs = this.presentation.slides.map(s => s.settings.thumbnail);
      this.historyIndex = nextIndex;
      const newState = JSON.parse(JSON.stringify(this.history[this.historyIndex]));

      newState.forEach((slide, i) => {
          if (currentThumbs[i]) slide.settings.thumbnail = currentThumbs[i];
      });

      this.presentation.slides = newState;
    },
    undoRedoCommonTasks() {
      if (this.selectedEditor) {
        this.selectedEditor.commands.blur();
        this.selectedEditor.setEditable(false);
      }
      if (this.selectedTable) {
        this.selectedTable.commands.blur();
      }
      this.onEditor = false;
      this.settings = "default";
      this.configSlides = false;
      this.showMenuSettings = false;
      this.indexElement = -1;
      this.unSelectElements();
    },
    async getIslamicCalendar(date, lat = 41, lon = -8, tenant = useAppStore().getCurrentTenant) {
      return new Promise((resolve, reject) => {
        axios.post(`islamic_calendar/?latitude=${lat}&longitude=${lon}&date=${date}&tenant=${tenant}`)
          .then((response) => {
            if (response?.status === 200) {
              const data = response.data;
              resolve(data);
            } else {
              reject([]);
            }
          })
          .catch(() => {
            reject([]);
          });
      });
    },
    async getExchangeRates(source, currency) {
      return new Promise((resolve, reject) => {
        axios
          .post("get_exchange_rates/", {
            source,
            currency,
          })
          .then((response) => {
            const data = response.data;
            resolve(data);
          })
          .catch(() => {
            reject([]);
          });
      });
    },
    async getTvChannels() {
      return new Promise((resolve, reject) => {
        APIRequest(
          "widgets/tvwidget/?page_size=999999&tenant=" +
            useAppStore().getCurrentTenant
        )
          .then((response) => {
            const data = response.data["results"];
            const urlList = [];
            data.forEach((element) => {
              urlList.push({
                id: element["id"],
                name: element["name"],
                value: element["url"],
              });
            });
            resolve(urlList);
          })
          .catch(() => {
            reject([]);
          });
      });
    },
    async saveTvChannel(name, url) {
      return new Promise((resolve, reject) => {
        APIRequest("widgets/tvwidget/", "post", {
          name: name,
          url: url,
          tenant: useAppStore().getCurrentTenant,
        })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    async updateTvChannel(id, name, url) {
      return new Promise((resolve, reject) => {
        APIRequest(`widgets/tvwidget/${id}/`, "patch", {
          name: name,
          url: url,
        })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    async deleteTvChannel(id) {
      return new Promise((resolve, reject) => {
        APIRequest(`widgets/tvwidget/${id}/`, "delete")
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    async getIFrameUrls() {
      return new Promise((resolve, reject) => {
        APIRequest(
          "widgets/iframe/?page_size=999999&tenant=" +
            useAppStore().getCurrentTenant
        )
          .then((response) => {
            // make a name and value array list
            const data = response.data["results"];
            const urlList = [];
            data.forEach((element) => {
              urlList.push({
                id: element["id"],
                name: element["name"],
                value: element["url"],
              });
            });

            resolve(urlList);
          })
          .catch(() => {
            reject([]);
          });
      });
    },
    async saveIFrame(name, url) {
      return new Promise((resolve, reject) => {
        APIRequest("widgets/iframe/", "post", {
          name: name,
          url: url,
          tenant: useAppStore().getCurrentTenant,
        })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    async updateIFrame(id, name, url) {
      return new Promise((resolve, reject) => {
        APIRequest(`widgets/iframe/${id}/`, "patch", {
          name: name,
          url: url,
        })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    async deleteIFrame(id) {
      return new Promise((resolve, reject) => {
        APIRequest(`widgets/iframe/${id}/`, "delete")
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    async openJson(url) {
      return new Promise((resolve, reject) => {
        axios
          .get(url)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    async convertToDefaultJson(presentation) {
      return {
        id: presentation.id,
        template: {
          id: presentation.id,
          thumbnail: `${presentation.thumbnail}`,
          json_file_path: `${presentation.json_file_path}`,
          created_at: presentation.created_at,
          updated_at: presentation.updated_at,
          orientation: presentation.orientation,
          type: presentation.type,
        },
        created_at: presentation.created_at,
        updated_at: presentation.updated_at,
        name: presentation.name,
        slide_amount: presentation.slide_amount,
        width: presentation.width,
        height: presentation.height,
        source_ref_id: presentation?.group,
      };
    },
    async openTemplate(id, group = false) {
      return new Promise((resolve, reject) => {
        APIRequest(
          `/compose/${group ? "group-template" : "presentation"}/${id}/`
        )
          .then((response) => {
            const result = response.data;
            
            if (
              !group &&
              String(useAppStore().getCurrentTenant) !==
                String(result.source_ref_id)
            ) {
              reject();
            }

            if (
              group &&
              String(useAppStore().getTenantGroup) !==
                String(result.group)
            ) {
              reject();
            }

            resolve(group ? this.convertToDefaultJson(result) : result);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    async saveGroupTemplate(title, playlists = []) {
      const payload = JSON.parse(JSON.stringify(this.presentation));

      await this.prepareThumbnailsForUpload(payload.slides);

      let formData = new FormData();

      formData.append(
        "json_file_path",
        new File([JSON.stringify(payload)], `${Date.now()}.json`, {
          type: "application/json",
        })
      );

      formData.append(
        "thumbnail",
        payload.slides[0].settings.thumbnail
      );

      formData.append("name", title);
      formData.append("playlists", JSON.stringify(playlists));
      formData.append("slide_amount", payload.slides.length);

      const response = await APIRequest(
        `compose/group-template/${this.presentationDetails.id}/`,
        "patch",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      return response.data;
    },
    async prepareThumbnailsForUpload(slides) {
        // Process all slides in parallel for speed
        await Promise.all(slides.map(async (slide) => {
            const thumb = slide.settings.thumbnail;
            
            // Check if it's a blob URL
            if (thumb && thumb.startsWith('blob:')) {
                try {
                    // Fetch the binary data from the blob store
                    const res = await fetch(thumb);
                    const blob = await res.blob();
                    
                    // Convert to Base64
                    slide.settings.thumbnail = await new Promise((resolve) => {
                        const reader = new FileReader();
                        reader.onloadend = () => resolve(reader.result);
                        reader.readAsDataURL(blob);
                    });
                } catch (e) {
                    // Fallback to empty string so we don't save an invalid blob string
                    slide.settings.thumbnail = '';
                    return;
                }
            }
        }));
    },
    async saveTemplate(title, playlists = []) {
      const payload = JSON.parse(JSON.stringify(this.presentation));
      await this.prepareThumbnailsForUpload(payload.slides);

      let formData = new FormData();

      formData.append(
        "template.thumbnail",
        payload.slides[0].settings.thumbnail
      );

      if (this.presentationDetails?.group_template) {
        // Calculate positions based on the payload (indexes are the same)
        const slide_length_without_g_lock = payload.slides.filter(
          (slide) => !slide?.settings?.g_lock
        ).length;
        
        const first_g_lock_index = payload.slides.findIndex(
          (slide) => slide?.settings?.g_lock
        );

        formData.append("slide_amount", slide_length_without_g_lock);
        formData.append("group_template_position", first_g_lock_index);

        // Filter slides from the Base64-ready payload
        const json = {
          ...payload,
          slides: payload.slides.filter((slide) => !slide?.settings?.g_lock)
        };

        formData.append(
          "template.json_file_path",
          new File([JSON.stringify(json)], `${Date.now()}.json`, {
            type: "application/json",
          })
        );
      } else {
        formData.append("slide_amount", payload.slides.length);

        // CRITICAL FIX: Use 'payload' here too
        formData.append(
          "template.json_file_path",
          new File(
            [JSON.stringify(payload)], // Save the version with Base64!
            `${Date.now()}.json`,
            {
              type: "application/json",
            }
          )
        );
      }

      formData.append(
        "template.orientation",
        this.presentationDetails.template.orientation
      );
      formData.append(
        "template.type",
        this.presentationDetails.template.type
      );
      formData.append("name", title);
      formData.append("playlists", JSON.stringify(playlists));

      try {
        const response = await APIRequest(
          `compose/presentation/${this.presentationDetails.id}/`,
          "patch",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
        return response.data;
      } catch (error) {
        console.log(error);
      }
    },
    addSlide() {
      this.presentation.slides.push({
        id: Date.now(),
        settings: {
          show: true,
          lock: false,
          background: {
            type: "color",
            value: "rgb(255,255,255)",
          },
          delay_type: false,
          transition: 10,
          animation: "fade",
          animation_time: 1,
          thumbnail: "",
        },
        elements: [],
      });
    },
    addElement(element) {
      if (
        this.presentation.slides[this.indexDataArray].settings.lock === false
      ) {
        this.presentation.slides[this.indexDataArray].elements.push(element);
        this.indexElement = this.slideElements.length - 1;
        nextTick(() => {
          this.handleScale();
          this.showMenuSettings = true;
        });
      }
    },
    deleteElement(index = this.indexElement) {
      if (
        this.presentation.slides[this.indexDataArray].settings.lock === false
      ) {
        let targetIndex = index;
        if (targetIndex === undefined || targetIndex === null) {
          targetIndex = this.indexElement;
        }
        if (typeof targetIndex !== "number" || targetIndex < 0 || targetIndex >= this.slideElements.length) {
          return;
        }
        this.presentation.slides[this.indexDataArray].elements.splice(targetIndex, 1);
        this.settings = "default";
        this.indexElement = -1;
        this.showMenuSettings = false;
        this.configListView = false;
        this.check = true;
      }
    },
    disableElements() {
      this.settings = "default";
      this.showMenuSettings = false;
      this.indexElement = -1;
      for (let i = 0; i < this.slideElements.length; i++) {
        this.slideElements[i].active = false;
      }
    },

    // Utility: throttle (move outside moveWidget)
    throttle(func) {
      let ticking = false;
      return function (...args) {
        if (!ticking) {
          window.requestAnimationFrame(() => {
            func.apply(this, args);
            ticking = false;
          });
          ticking = true;
        }
      };
    },
    // MOVE WIDGETS
    moveWidget(event, multiple = false) {
      const { x: max_x, y: max_y } = this.getResolution;
      this.interacting = true;

      const { clientX: initialX, clientY: initialY } = event;

      let mousemove = null;

      if (multiple) {
        const selectedElements = this.getSelectedItems;
        if (!selectedElements.length) return;

        const initialPositions = selectedElements.map((el) => ({
          el,
          x: el.left,
          y: el.top,
        }));

        // Calculate the group bounds ONCE at the start of the drag
        let groupMinX = Infinity, groupMaxX = -Infinity;
        let groupMinY = Infinity, groupMaxY = -Infinity;
        
        initialPositions.forEach(({ el, x, y }) => {
          if (x < groupMinX) groupMinX = x;
          if (x + el.width > groupMaxX) groupMaxX = x + el.width;
          if (y < groupMinY) groupMinY = y;
          if (y + el.height > groupMaxY) groupMaxY = y + el.height;
        });

        // Calculate max allowed deltas based on the overall group bounds
        const maxDeltaLeft = -groupMinX;
        const maxDeltaRight = max_x - groupMaxX;
        const maxDeltaUp = -groupMinY;
        const maxDeltaDown = max_y - groupMaxY;

        mousemove = this.throttle((e) => {
          const rawDX = (e.clientX - initialX) / this.zoom;
          const rawDY = (e.clientY - initialY) / this.zoom;

          // Clamp delta using the pre-calculated group boundaries
          const dx = Math.min(Math.max(rawDX, maxDeltaLeft), maxDeltaRight);
          const dy = Math.min(Math.max(rawDY, maxDeltaUp), maxDeltaDown);

          // Batch all element coordinate writes inside a single Pinia patch transaction!
          this.$patch(() => {
            initialPositions.forEach(({ el, x, y }) => {
              el.left = x + dx;
              el.top = y + dy;
            });
          });
        });
      } else {
        const el = this.selectedWidget;
        if (!el) return;

        const { left: initialElementX, top: initialElementY } = el;
        const maxRight = max_x - el.width;
        const maxBottom = max_y - el.height;

        mousemove = this.throttle((e) => {
          const deltaX = (e.clientX - initialX) / this.zoom;
          const deltaY = (e.clientY - initialY) / this.zoom;

          // Faster clamping without extra function calls
          let newLeft = initialElementX + deltaX;
          let newTop = initialElementY + deltaY;
          
          el.left = newLeft < 0 ? 0 : (newLeft > maxRight ? maxRight : newLeft);
          el.top = newTop < 0 ? 0 : (newTop > maxBottom ? maxBottom : newTop);
        });
      }

      const mouseup = () => {
        this.addToHistory();
        this.interacting = false;
        document.removeEventListener("mousemove", mousemove);
        document.removeEventListener("mouseup", mouseup);
      };

      document.addEventListener("mousemove", mousemove);
      document.addEventListener("mouseup", mouseup);
    },

    // Resize element
    resizeWidget(event, position) {
      this.interacting = true;

      const canvasWidth = this.getResolution["x"];
      const canvasHeight = this.getResolution["y"];

      const selectedElements = this.getSelectedItems;
      if (!selectedElements.length) return;

      const groupBoundingBox = this.calculateGroupBoundingBox(selectedElements);
      const groupWidth = groupBoundingBox.right - groupBoundingBox.left;
      const groupHeight = groupBoundingBox.bottom - groupBoundingBox.top;
      const aspectRatio = groupWidth / groupHeight;

      const minGroupWidth = selectedElements.reduce(
        (max, el) => Math.max(max, el.min_width || 0),
        100
      );

      const initialX = event.clientX;
      const initialSizes = selectedElements.map((el) => ({
        el,
        width: el.width,
        height: el.height,
        offsetX: el.left - groupBoundingBox.left,
        offsetY: el.top - groupBoundingBox.top,
      }));

      const mousemove = this.throttle((e) => {
        const deltaX = (e.clientX - initialX) / this.zoom;

        let newLeft = groupBoundingBox.left;
        let newTop = groupBoundingBox.top;
        let newGroupWidth = groupWidth;
        let newGroupHeight = groupHeight;

        // Handle resizing logic for each corner
        if (position === "tl") {
          newGroupWidth = Math.max(minGroupWidth, groupWidth - deltaX);
          newGroupHeight = newGroupWidth / aspectRatio;
          newLeft = groupBoundingBox.right - newGroupWidth;
          newTop = groupBoundingBox.bottom - newGroupHeight;
        } else if (position === "tr") {
          newGroupWidth = Math.max(minGroupWidth, groupWidth + deltaX);
          newGroupHeight = newGroupWidth / aspectRatio;
          newTop = groupBoundingBox.bottom - newGroupHeight;
        } else if (position === "bl") {
          newGroupWidth = Math.max(minGroupWidth, groupWidth - deltaX);
          newGroupHeight = newGroupWidth / aspectRatio;
          newLeft = groupBoundingBox.right - newGroupWidth;
        } else if (position === "br") {
          newGroupWidth = Math.max(minGroupWidth, groupWidth + deltaX);
          newGroupHeight = newGroupWidth / aspectRatio;
        }

        // Enforce canvas boundaries for the group
        if (newLeft < 0) {
          // Clamp newLeft to 0 and adjust newGroupWidth
          newLeft = 0;

          // Recalculate newGroupWidth based on fixed right boundary
          newGroupWidth = groupBoundingBox.right;

          // Maintain aspect ratio if applicable
          newGroupHeight = newGroupWidth / aspectRatio;

          // Only adjust newTop for top handles ('tl' or 'tr')
          if (position === "tl" || position === "tr") {
            newTop = groupBoundingBox.bottom - newGroupHeight;
          }
        }

        if (newTop < 0) {
          // Clamp newTop to 0 and adjust newGroupHeight
          newTop = 0;

          // Recalculate newGroupHeight based on fixed bottom boundary
          newGroupHeight = groupBoundingBox.bottom;

          // Maintain aspect ratio if applicable
          newGroupWidth = newGroupHeight * aspectRatio;

          // Only adjust newLeft for left handles ('tl' or 'bl')
          if (position === "tl" || position === "bl") {
            newLeft = groupBoundingBox.right - newGroupWidth;
          }
        }

        if (newLeft + newGroupWidth > canvasWidth) {
          // Clamp newGroupWidth to the right boundary
          newGroupWidth = canvasWidth - newLeft;

          // Maintain aspect ratio if applicable
          newGroupHeight = newGroupWidth / aspectRatio;

          // Only adjust newTop for top handles ('tl' or 'tr')
          if (position === "tl" || position === "tr") {
            newTop = groupBoundingBox.bottom - newGroupHeight;
          }
        }

        if (newTop + newGroupHeight > canvasHeight) {
          // Clamp newGroupHeight to the bottom boundary
          newGroupHeight = canvasHeight - newTop;

          // Maintain aspect ratio if applicable
          newGroupWidth = newGroupHeight * aspectRatio;

          // Only adjust newLeft for left handles ('tl' or 'bl')
          if (position === "tl" || position === "bl") {
            newLeft = groupBoundingBox.right - newGroupWidth;
          }
        }

        // Ensure no element goes below its minimum size
        let minWidthViolated = false;
        let minHeightViolated = false;

        const widthRatio = newGroupWidth / groupWidth;
        const heightRatio = newGroupHeight / groupHeight;

        selectedElements.forEach((_, i) => {
          const { el, width, height } = initialSizes[i];

          const newWidth = Math.max(el.min_width || 0, width * widthRatio);
          const newHeight = Math.max(el.min_height || 0, height * heightRatio);

          if (newWidth <= el.min_width) minWidthViolated = true;
          if (newHeight <= el.min_height) minHeightViolated = true;
        });

        if (minWidthViolated || minHeightViolated) {
          return; // Halt resizing if constraints are violated
        }

        // Batch all resizing coordinate and size writes inside a single Pinia patch transaction!
        this.$patch(() => {
          selectedElements.forEach((_, i) => {
            const { el, width, height, offsetX, offsetY } = initialSizes[i];

            el.width = Math.max(el.min_width || 0, width * widthRatio);
            el.height = Math.max(el.min_height || 0, height * heightRatio);

            if (position.includes("l") || position.includes("r")) {
              el.left = newLeft + offsetX * widthRatio;
            }
            if (position.includes("t") || position.includes("b")) {
              el.top = newTop + offsetY * heightRatio;
            }
          });
        });
      });

      const mouseup = () => {
        this.addToHistory();

        this.interacting = false;
        document.removeEventListener("mousemove", mousemove);
        document.removeEventListener("mouseup", mouseup);
      };

      document.addEventListener("mousemove", mousemove);
      document.addEventListener("mouseup", mouseup);
    },
    calculateGroupBoundingBox(elements) {
      return elements.reduce(
        (box, el) => ({
          left: Math.min(box.left, el.left),
          top: Math.min(box.top, el.top),
          right: Math.max(box.right, el.left + el.width),
          bottom: Math.max(box.bottom, el.top + el.height),
        }),
        { left: Infinity, top: Infinity, right: -Infinity, bottom: -Infinity }
      );
    },
    unSelectElements() {
      this.indexElement = -1;
      this.configListView = false;
      this.handleUnselect();
    },
    handleUnselect() {
      this.selectedItemNum = 0;
      this.getSelectedItems.forEach((element) => {
        element.isSelected = false;
      });
    },
    setImageAsBackground() {
      if (!this.selectedWidget.src) return;

      this.currentSlide.settings.background.opacity =
        this.selectedWidget?.settings?.opacity || 100;
      this.currentSlide.settings.background.color =
        this.currentSlide.settings.background.value;
      this.currentSlide.settings.background.type = "image";
      this.currentSlide.settings.background.value = this.selectedWidget.src;
      this.currentSlide.settings.background.flip =
        this.selectedWidget?.flip || false;
      this.deleteElement(this.indexElement);
      this.configListView = false;
      this.settings = "default";
      this.check = true;
      this.addToHistory();
    },
    unsetImageAsBackground() {
      const bg = this.currentSlide.settings.background;
      if (bg.type === "color") return;
      const { color, value, opacity, flip } = bg;

      // Reset slide background to color
      this.currentSlide.settings.background = {
        type: "color",
        value: color || "rgb(255, 255, 255)",
        color: color || "rgb(255, 255, 255)",
        opacity: 100,
        flip: false,
      };

      // Create new image widget from background image
      const baseWidget = widgets.find((el) => el.component === "ImageWidget");
      if (!baseWidget) return;

      const newWidget = {
        ...baseWidget,
        id: Date.now(),
        name: baseWidget.component,
        src: value,
        flip,
        top: 0,
        left: 0,
        width: 400,
        height: 400,
        active: true,
        settings: {
          ...baseWidget.settings,
          opacity: opacity || 100,
        },
      };

      this.addElement(newWidget);
      this.configListView = false;
      this.addToHistory();
    },
    async addBackup(id, newData) {
      this.savingBackup = true;

      // Filter out group slides
      const normalSlides = (newData.slides || []).filter(
        (slide) => !slide.settings?.g_lock
      );

      // If no normal slides, skip backup
      if (!normalSlides.length) {
        console.warn(`Backup skipped for ID "${id}" — no normal slides found`);
        this.savingBackup = false;
        return;
      }

      await this.prepareThumbnailsForUpload(newData.slides);

      // 1. Manage backup IDs
      let backupIds = (await get(BACKUP_KEYS_KEY)) || [];
      backupIds = backupIds.filter((existingId) => existingId !== id);
      backupIds.unshift(id);

      while (backupIds.length > MAX_IDS) {
        const removedId = backupIds.pop();
        if (removedId) {
          await del(removedId);
        }
      }
      await set(BACKUP_KEYS_KEY, backupIds);

      // 2. Load current backup list
      let currentDataList = await get(id);
      if (!Array.isArray(currentDataList)) {
        currentDataList =
          currentDataList !== undefined ? [currentDataList] : [];
      }

      // 3. Find first group slide position
      let groupTemplatePosition = 0;
      const firstLockedIndex = newData.slides.findIndex(
        (slide) => slide.settings?.g_lock
      );
      if (firstLockedIndex >= 0) {
        groupTemplatePosition = firstLockedIndex;
      }

      // 4. Create backup entry
      const backupEntry = JSON.parse(
        JSON.stringify({
          ...newData,
          slides: normalSlides,
          timestamp: Date.now(),
          group_template_position: groupTemplatePosition,
        })
      );

      // 5. Save backup
      currentDataList.unshift(backupEntry);
      currentDataList = currentDataList.slice(0, MAX_ENTRIES);
      await set(id, currentDataList);

      console.log(
        `Saved backup for ID "${id}" (group position = ${groupTemplatePosition})`
      );
      setTimeout(() => {
        this.savingBackup = false;
      }, 3000);
    },
    async backupList(id) {
      // return list, and check if empty or undefined
      let currentDataList = await get(id);
      if (!Array.isArray(currentDataList)) {
        currentDataList =
          currentDataList !== undefined ? [currentDataList] : [];
      }
      return currentDataList;
    },
    async deleteBackup(id) {
      await del(id);
      let backupIds = (await get(BACKUP_KEYS_KEY)) || [];
      backupIds = backupIds.filter((existingId) => existingId !== id);
      await set(BACKUP_KEYS_KEY, backupIds);
      console.log(`Deleted backup for ID "${id}"`);
    },
    getPlaylistIds() {
      // get all playlists from all slides
      const playlistIds = new Set();
      this.presentation.slides.forEach((slide) => {
        slide.elements.forEach((element) => {
          if (
            element.name === "MediaPlaylistWidget" &&
            element.settings?.playlist
          ) {
            playlistIds.add(element.settings.playlist);
          }
          else if(
            element.name === "WayFinderWidget" &&
            element?.settings?.fallback?.enabled &&
            element?.settings?.fallback?.playlist
          ){
            playlistIds.add(element.settings.fallback.playlist)
          }
        });
      });
      // Add presentation audio ID if it exists
      if (
        this.presentation?.audio &&
        this.presentation?.audio?.enabled &&
        this.presentation?.audio?.id
      ) {
        playlistIds.add(this.presentation.audio.id);
      }

      return Array.from(playlistIds);
    },
    async savePresentationToIndexedDB() {
      let presentation = JSON.parse(JSON.stringify(this.presentation));

      presentation.width = this.presentationDetails.width;
      presentation.height = this.presentationDetails.height;
      presentation.audio_preview = this.currentAudioPlaylist;
    
      await set(`presentation_preview`, presentation);
    },
    getAspectRatio(width, height) {
      if (!width || !height) return '';

      const decimalRatio = width / height;

      const commonRatios = [
        {string: "16:9", value: 16 / 9},
        {string: "16:10", value: 16 / 10},
        {string: "4:3", value: 4 / 3},
        {string: "1:1", value: 1},
        {string: "21:9", value: 21 / 9},
        {string: "3:2", value: 3 / 2},
        {string: "9:16", value: 9 / 16},
      ];

      //use common ratios if we're close enough to one of them
      for (const ratio of commonRatios) {
        if (Math.abs(decimalRatio - ratio.value) < 0.05) {
          return ratio.string
        }
      }

      //get greatest common divisor (gcd)
      const getGcd = (a, b) => b === 0 ? a : getGcd(b, a % b);

      const divisor = getGcd(width, height);

      const ratioWidth = width / divisor;
      const ratioHeight = height / divisor;
      
      return `${ratioWidth}:${ratioHeight}`;
    }
  },
  getters: {
    async getPresentationFromIndexedDB() {
      return await get(`presentation_preview`) || null;
    },
    isEPaper() {
      const details = this.presentationDetails;
      return !!(details?.template?.epaper || details?.epaper);
    },
    getResolution() {
      const RESOLUTION_X =
        (this.presentation?.orientation === "portrait"
          ? this.presentationDetails?.height
          : this.presentationDetails?.width) || 1920;
      const RESOLUTION_Y =
        (this.presentation?.orientation === "portrait"
          ? this.presentationDetails?.width
          : this.presentationDetails?.height) || 1080;

      return {
        x: RESOLUTION_X,
        y: RESOLUTION_Y,
      };
    },
    getSelectedItems() {
      return this.slideElements.filter((element) => element.isSelected);
    },
    getSelectedItemsGroup() {
      // Check if all elements have the same group_id and it's not undefined
      const groupIds = this.getSelectedItems.map((element) => element.group_id);

      // If any element does not have a group_id, return false
      if (groupIds.includes(undefined)) {
        return false;
      }

      // Check if all group_ids are the same
      const isSameGroup = groupIds.every((groupId) => groupId === groupIds[0]);

      return isSameGroup;
    },
    getSelectedItemsGroupId() {
      const groupIds = this.getSelectedItems.map((element) => element.group_id);
      if (groupIds.includes(undefined)) return null;
      return groupIds[0];
    },
    canUndo() {
      return this.historyIndex > 0;
    },
    canRedo() {
      return this.historyIndex < this.history.length - 1;
    },
    isSlideLocked() {
      return (
        this.presentation?.slides?.[this.indexDataArray]?.settings?.lock ||
        this.presentation?.slides?.[this.indexDataArray]?.settings?.admin_lock ||
        this.presentation?.slides?.[this.indexDataArray]?.settings?.g_lock
      );
    },
    isWidgetLocked() {
      return this.selectedWidget?.locked || this.selectedWidget?.admin_locked;
    },
    slideElements() {
      return this.presentation?.slides?.[this.indexDataArray]?.elements || [];
    },
    currentSlide() {
      return this.presentation?.slides?.[this.indexDataArray];
    },
    selectedWidget() {
      if (!this.presentation) return null;
      return this.slideElements[this.indexElement];
    },
    checkNBrowserWidgets() {
      let found = false;
      this.slideElements.forEach((element) => {
        if (
          ["VideoWidget", "TVWidget"].includes(element.name) ||
          this.videoPlaylistExists
        )
          found = true;
      });

      if (found) return true;
      return false;
    },
    hasVideoWidget() {
      if (!this.slideElements) return false;
      if (this.slideElements.find((element) => element.name === "VideoWidget"))
        return true;
      return false;
    },
    hasMediaWidgets(){
      const hasMediaWidgets = this.presentation?.slides?.some(slide => 
        slide.elements?.some(element => 
          element.component === "VideoWidget" || 
          element.component === "TVWidget" || 
          (element.component === "MediaPlaylistWidget" && element?.settings?.playlist_type === "Video") ||
          (element.component === "WayFinderWidget" && element?.settings?.fallback?.enabled && element?.settings?.fallback?.playlist && element?.settings?.fallback?.playlist_type === "Video")
        )
      );

      return hasMediaWidgets
    },
    currentWidgetNBrowser() {
      if (this.selectedItemNum > 1) {
        let found = false;

        this.getSelectedItems.forEach((element) => {
          if (
            ["VideoWidget", "TVWidget", "MediaPlaylistWidget", "WayFinderWidget"].includes(
              element.name
            )
          )
            found = true;
        });
        if (found) return true;
        return false;
      }

      if (!this.slideElements[this.indexElement]) return false;
      if (
        ["VideoWidget", "TVWidget", "MediaPlaylistWidget", "WayFinderWidget"].includes(
          this.slideElements[this.indexElement].name
        )
      )
        return true;
      return false;
    },
    hasLockPermissions() {
      return ["client-admin", "nonius-admin"].includes(
        useAppStore().getCurrentRole
      );
    },
    rightMenuOpen() {
      return this.showGallery || this.settings == "TVWidget";
    },
    videoPlaylistExists() {
      return this.slideElements.some((element) => {
        // Condition 1: MediaPlaylistWidget
        const isMediaPlaylist = 
          element.name === "MediaPlaylistWidget" &&
          element?.settings?.playlist &&
          element?.settings?.playlist_type === "Video";

        // Condition 2: WayfinderWidget (Added optional chaining for safety)
        const isWayfinderFallback = 
          element.name === "WayFinderWidget" &&
          element?.settings?.fallback?.enabled &&
          element?.settings?.fallback?.playlist &&
          element?.settings?.fallback?.playlist_type === "Video";

        return isMediaPlaylist || isWayfinderFallback;
      });
    },
    defaultSlideExists() {
      // method to check if there's at least one slide without g_lock
      return this.presentation.slides.some((slide) => !slide.settings?.g_lock && !slide.settings?.admin_lock);
    }
  },
});
